import {
  Flag,
  countryName,
  getPlan,
  getStats,
  listTrips,
  Pick,
  Popover,
  posterAddress,
  toIso,
  type Badge,
  type Mode,
  type Stats as StatsScreen,
  type TripSummary,
} from '@trippath/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { MapPanel } from '../../components/map/MapPanel';
import { TripTree } from '../../components/family/TripTree';
import { dayRange } from '../../lib/countryFacts';
import {
  drawFrame,
  legLine,
  projector,
  recordVideo,
  routeOf,
  WAY_COLOURS,
  type Look,
  type Painter,
  type Route,
  type RouteStop,
} from '../../lib/routeVideo';

type State = { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; stats: StatsScreen };

/** Each mode's sign beside its name. */
const SIGNS: Record<Mode, string> = {
  flight: '✈',
  train: '🚆',
  bus: '🚌',
  ferry: '⛴',
  car: '🚗',
  foot: '🚶',
};

/** Each achievement's sign. */
const BADGE_SIGNS: Record<string, string> = {
  'first-trip': '🎒',
  'four-countries': '🗺',
  'fast-train': '🚄',
  'night-train': '🌙',
  ferry: '⛴',
  'long-flight': '✈',
  climb: '⛰',
  slow: '🐢',
  equator: '🧭',
  continents: '🌏',
  'countries-10': '🏳',
  'countries-25': '🏆',
  'nights-100': '🛏',
  'train-10000': '🚂',
  'foot-100': '🥾',
  'around-the-world': '🌐',
};

/** The colours the year's countries are told apart by, in turn. */
const COUNTRY_COLOURS = [
  '#dc2626',
  '#2563eb',
  '#d97706',
  '#059669',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#65a30d',
];

/** A video's length, and its preview's. */
const VIDEO_MS = 30_000;
const PREVIEW_MS = 8_000;
const VIDEO_SIZE: [number, number] = [1920, 1080];

/** A trip's days with their year: `1–21 Mar 2027`. */
const tripDays = (start: string, end: string, lang: string) =>
  new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatRange(new Date(`${start}T00:00:00Z`), new Date(`${end}T00:00:00Z`));

/** Whole kilometres, grouped: `14 830 km`. */
const kmOf = (km: number, lang: string) => `${Math.round(km).toLocaleString(lang)} km`;

/** Each day of a year in a month's grid, coloured by the country slept in. */
function YearOf({ stats, year }: { stats: StatsScreen; year: number }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const countries = [...new Set(stats.stays.map((stay) => stay.country))];
  const colour = (country: string) =>
    COUNTRY_COLOURS[countries.indexOf(country) % COUNTRY_COLOURS.length];
  const pad = (n: number) => String(n).padStart(2, '0');
  const months = Array.from({ length: 12 }, (_, month) => {
    const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const lead = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
    const days = Array.from({ length }, (_, at) => {
      const day = `${String(year)}-${pad(month + 1)}-${pad(at + 1)}`;
      const stay = stats.stays.find((one) => day >= one.start && day < one.end);
      return { day, country: stay?.country };
    });
    const name = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month, 1)));
    return { month, lead, days, name };
  });
  return (
    <section className="panel" aria-labelledby="stats-year">
      <div className="panel-head">
        <h2 id="stats-year" className="panel-title">
          {t('stats.byCountry', { year })}
        </h2>
        <ul className="stats-key">
          {countries.map((country) => (
            <li key={country}>
              <i style={{ background: colour(country) }} aria-hidden="true" />
              <Flag country={country} />
              {countryName(country, lang)}
            </li>
          ))}
        </ul>
      </div>
      <div className="stats-months">
        {months.map(({ month, lead, days, name }) => (
          <div key={month} className="stats-month">
            <span>{name}</span>
            <div className="stats-days">
              {Array.from({ length: lead }, (_, at) => (
                <i key={`lead-${String(at)}`} className="stats-blank" />
              ))}
              {days.map(({ day, country }) => (
                <i
                  key={day}
                  title={country ? `${day} · ${countryName(country, lang)}` : day}
                  style={country ? { background: colour(country) } : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** An achievement, earned or how far along. */
function BadgeCard({ badge }: { badge: Badge }) {
  const { t, i18n } = useTranslation();
  return (
    <li className={badge.done ? 'stats-badge' : 'stats-badge stats-locked'}>
      <span className="stats-sign" aria-hidden="true">
        {BADGE_SIGNS[badge.key]}
      </span>
      <b>{t(`stats.badge.${badge.key}.name`)}</b>
      <small>
        {badge.done && badge.by
          ? badge.by
          : badge.need > 1
            ? t('stats.progress', {
                have: badge.have.toLocaleString(i18n.language),
                need: badge.need.toLocaleString(i18n.language),
              })
            : t(`stats.badge.${badge.key}.how`)}
      </small>
      {!badge.done && (
        <span className="stats-progress">
          <span style={{ width: `${String(Math.round((badge.have / badge.need) * 100))}%` }} />
        </span>
      )}
    </li>
  );
}

/** A trip's poster as it will look, drawn in the page. */
function PosterPreview({ route, look, size }: { route: Route; look: Look; size: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const at = projector(route.stops, 300, 300, 24);
  const nights = route.stops.reduce((sum, stop) => sum + stop.nights, 0);
  const km = route.legs.reduce((sum, leg) => sum + leg.km, 0);
  const first = route.stops[0] as RouteStop;
  return (
    <figure
      className={look.dark ? 'stats-poster' : 'stats-poster stats-light'}
      aria-label={t('stats.posterPreview', { size })}
    >
      <div>
        <h3>{route.name}</h3>
        <small>
          {dayRange(first.start, route.end, lang)} ·{' '}
          {t('stats.posterLine', { nights, km: kmOf(km, lang) })}
        </small>
      </div>
      <svg viewBox="0 0 300 300" role="img" aria-label={t('stats.posterMap')}>
        {route.legs.map((leg) => {
          const line = legLine(route, leg, at, look.arcs);
          return (
            <polyline
              key={`${String(leg.from)}-${String(leg.to)}`}
              points={line.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
              fill="none"
              stroke={WAY_COLOURS[leg.mode ?? 'none']}
              strokeWidth={2}
              strokeDasharray={leg.mode === 'flight' ? '5 3' : undefined}
            />
          );
        })}
        {route.stops.map((stop) => {
          const [x, y] = at(stop);
          return (
            <g key={`${stop.city}-${stop.start}`}>
              <circle cx={x} cy={y} r={3} fill="currentColor" />
              {look.names && stop.nights > 0 && (
                <text x={x + 5} y={y + 3}>
                  {stop.city}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <small>
        {route.stops
          .filter((stop) => stop.nights > 0)
          .map((stop) => stop.city.toUpperCase())
          .join(' · ')}
      </small>
    </figure>
  );
}

/** A choice of a few, one pressed. */
function Choice<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: [T, string][];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="stats-field">
      <span>{label}</span>
      <div className="segmented segmented-small" role="group" aria-label={label}>
        {options.map(([option, said]) => (
          <button
            key={option}
            type="button"
            aria-pressed={option === value}
            onClick={() => {
              onChange(option);
            }}
          >
            {said}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The route video's buttons: a preview, the video made, and the file kept. */
function VideoMaker({ name, play }: { name: string; play: (ms: number) => Promise<Blob> }) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [video, setVideo] = useState<{ address: string; type: string } | null>(null);
  const make = () => {
    setRecording(true);
    void play(VIDEO_MS).then((blob) => {
      setVideo({ address: URL.createObjectURL(blob), type: blob.type.replace('video/', '') });
      setRecording(false);
    });
  };
  return (
    <>
      {video && (
        <a className="link-button" href={video.address} download={`${name}.${video.type}`}>
          {t('stats.saveVideo')}
        </a>
      )}
      <div className="stats-actions">
        <button
          type="button"
          className="button"
          disabled={recording}
          onClick={() => {
            void play(PREVIEW_MS);
          }}
        >
          {t('stats.preview')}
        </button>
        <button
          type="button"
          className="button button-primary"
          disabled={recording}
          onClick={make}
        >
          {recording ? t('stats.recording') : t('stats.makeVideo')}
        </button>
      </div>
    </>
  );
}

/** The poster and the route video of a trip: how it looks, a preview, and the file. */
function Studio({ trips }: { trips: StatsScreen['trips'] }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [trip, setTrip] = useState(trips[0]?.id ?? '');
  // Its route once had: none when the trip cannot be had or has no stop on the map.
  const [route, setRoute] = useState<Route | null | undefined>(undefined);
  const [making, setMaking] = useState<'poster' | 'video'>('poster');
  const [size, setSize] = useState<'a4' | 'a3' | '50x70'>('a3');
  const [dark, setDark] = useState(true);
  const [names, setNames] = useState(true);
  const [arcs, setArcs] = useState(true);
  const canvas = useRef<HTMLCanvasElement>(null);
  const look: Look = { dark, names, arcs };
  useEffect(() => {
    if (!trip) return;
    let live = true;
    getPlan(trip).then(
      (plan) => {
        const made = routeOf(plan);
        if (live) setRoute(made.stops.length > 0 ? made : null);
      },
      () => {
        if (live) setRoute(null);
      },
    );
    return () => {
      live = false;
    };
  }, [trip]);
  /** A frame of the video at `t`, on the canvas when it is shown and the route is had. */
  const frame = (t: number) => {
    const context = canvas.current?.getContext('2d') as Painter | null | undefined;
    if (context && route) {
      drawFrame(context, route, look, t, VIDEO_SIZE, (day, km) =>
        [dayRange(day, day, lang), kmOf(km, lang)].join(' · '),
      );
    }
  };
  // The video's first frame, before it plays.
  useEffect(() => {
    frame(0);
  });
  if (trips.length === 0) return <p className="quiet">{t('stats.noTrips')}</p>;
  const yesNo = (on: boolean, flip: (on: boolean) => void, said: string, more?: string) => (
    <label className="check">
      <input
        type="checkbox"
        checked={on}
        onChange={(event) => {
          flip(event.target.checked);
        }}
      />
      <span>
        {said}
        {more && <small className="quiet"> · {more}</small>}
      </span>
    </label>
  );
  return (
    <div className="stats-studio">
      {making === 'poster' ? (
        route ? (
          <PosterPreview route={route} look={look} size={t(`stats.size.${size}`)} />
        ) : (
          <p className="quiet">{t(route === undefined ? 'trips.loading' : 'stats.noMap')}</p>
        )
      ) : (
        <div className="stats-video-box">
          <canvas
            ref={canvas}
            width={VIDEO_SIZE[0]}
            height={VIDEO_SIZE[1]}
            aria-label={t('stats.videoPreview')}
          />
        </div>
      )}
      <div className="stats-studio-side">
        <Pick
          label={t('stats.trip')}
          searchable={false}
          options={trips.map((one) => ({ value: one.id, label: one.name }))}
          value={trip}
          onChange={(chosen) => {
            // The one shown chosen again: its route kept, not asked for again.
            if (chosen === trip) return;
            setTrip(chosen);
            setRoute(undefined);
          }}
        />
        <div className="stats-choices">
          <Choice
            label={t('stats.making')}
            options={[
              ['poster', t('stats.poster')],
              ['video', t('stats.video')],
            ]}
            value={making}
            onChange={setMaking}
          />
          {making === 'poster' && (
            <Choice
              label={t('stats.sizeLabel')}
              options={(['a4', 'a3', '50x70'] as const).map((one) => [
                one,
                t(`stats.size.${one}`),
              ])}
              value={size}
              onChange={setSize}
            />
          )}
          <Choice
            label={t('stats.ground')}
            options={[
              ['dark', t('stats.dark')],
              ['light', t('stats.light')],
            ]}
            value={dark ? 'dark' : 'light'}
            onChange={(said) => {
              setDark(said === 'dark');
            }}
          />
        </div>
        {yesNo(names, setNames, t('stats.names'))}
        {yesNo(arcs, setArcs, t('stats.arcs'), t('stats.arcsOff'))}
        <p className="small quiet">
          {t(making === 'poster' ? 'stats.posterNote' : 'stats.videoNote')}
        </p>
        {making === 'poster' ? (
          <div className="stats-actions">
            <a
              className="button button-primary"
              href={posterAddress(trip, { size, dark, names, arcs }, lang)}
            >
              {t('stats.makePoster')}
            </a>
          </div>
        ) : (
          route && (
            <VideoMaker
              key={trip}
              name={route.name}
              play={(ms) => recordVideo(canvas.current as HTMLCanvasElement, frame, ms)}
            />
          )
        )}
      </div>
    </div>
  );
}

/** Where one has been, on the map: a heatmap of every point the trips hold, or the rest of the
 * world under fog. */
function ExploredMap({ explored }: { explored: StatsScreen['explored'] }) {
  const { t, i18n } = useTranslation();
  const [fog, setFog] = useState(false);
  const { cells, cell } = explored;
  // A cell's area: its side in kilometres, narrower away from the equator.
  const km2 = cells.reduce(
    (sum, [, lat]) => sum + (cell * 111.32) ** 2 * Math.cos((lat * Math.PI) / 180),
    0,
  );
  return (
    <section className="panel stats-explored" aria-labelledby="stats-explored">
      <div className="panel-head">
        <h2 id="stats-explored" className="panel-title">
          {t('stats.explored')}
        </h2>
        <div
          className="segmented segmented-small"
          role="group"
          aria-label={t('stats.exploredAs')}
        >
          {([false, true] as const).map((one) => (
            <button
              key={String(one)}
              type="button"
              aria-pressed={fog === one}
              onClick={() => {
                setFog(one);
              }}
            >
              {t(one ? 'stats.fog' : 'stats.heat')}
            </button>
          ))}
        </div>
      </div>
      {cells.length === 0 ? (
        <p className="quiet">{t('stats.noExplored')}</p>
      ) : (
        <>
          <p className="quiet">
            {t('stats.exploredSize', {
              cells: cells.length.toLocaleString(i18n.language),
              km2: Math.round(km2).toLocaleString(i18n.language),
            })}
          </p>
          <div className="stats-explored-map">
            <MapPanel
              data={{ points: [], lines: [], explored: { cell, cells, fog } }}
              label={t('stats.exploredMap')}
              fit={cells.map(([lon, lat]) => ({ lat, lon }))}
              keepView
              closest={10}
              // Zoomed by the wheel alone, as a map one came to look at.
              cooperative={false}
            />
          </div>
          {!fog && (
            // What its colours say: blue where little was recorded, red where most was.
            <div className="stats-heat-key">
              <span>{t('stats.heatFew')}</span>
              <span className="stats-heat-ramp" aria-hidden="true" />
              <span>{t('stats.heatMany')}</span>
              <span className="quiet">{t('stats.heatWhat')}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Statistics across one's trips (the `Stats` mockup): kilometres by how they were gone, nights
 * away, countries and towns, the footprint, the longest and the highest, each day of a year by
 * its country, achievements, and a trip's poster or route video.
 */
export function Stats() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [today] = useState(() => toIso(new Date()));
  const [year, setYear] = useState<number | 'all' | undefined>(undefined);
  // One trip counted alone, as the address says: `/stats?trip=<id>`.
  const [params, setParams] = useSearchParams();
  const trip = params.get('trip') ?? undefined;
  const [state, setState] = useState<State>({ kind: 'loading' });
  // The trips as their families, to choose one of them from.
  const [summaries, setSummaries] = useState<TripSummary[]>([]);
  const [choosing, setChoosing] = useState(false);
  useEffect(() => {
    listTrips().then(setSummaries, () => undefined);
  }, []);
  useEffect(() => {
    let live = true;
    getStats(today, year, trip).then(
      (stats) => {
        if (live) setState({ kind: 'ready', stats });
      },
      () => {
        if (live) setState({ kind: 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [today, year, trip]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind === 'failed') {
    return (
      <p className="page form-failed" role="alert">
        {t('stats.failed')}
      </p>
    );
  }
  const { stats } = state;
  const alone = stats.trips.find((one) => one.id === stats.trip);
  const shown = alone ? null : (stats.year ?? 'all');
  // The year whose days are coloured: the one chosen, or the one the trip counted alone starts.
  const calendar = alone ? Number(alone.start.slice(0, 4)) : stats.year;
  const train = stats.modes.find((mode) => mode.mode === 'train');
  const share = (part: number, whole: number) => Math.round((part / Math.max(whole, 1)) * 100);
  const widest = Math.max(...stats.modes.map((mode) => mode.km), 1);
  const hours = (minutes: number) =>
    t('stats.hours', {
      hours: Math.floor(minutes / 60),
      minutes: String(minutes % 60).padStart(2, '0'),
    });
  const longest: [string, string, string][] = [
    ...(stats.longestFlight
      ? [
          [
            t('stats.longestFlight'),
            stats.longestFlight.name,
            kmOf(stats.longestFlight.value, lang),
          ] as [string, string, string],
        ]
      : []),
    ...(stats.longestTrain
      ? [
          [
            t('stats.longestTrain'),
            stats.longestTrain.name,
            stats.longestTrain.minutes === null
              ? kmOf(stats.longestTrain.km, lang)
              : hours(stats.longestTrain.minutes),
          ] as [string, string, string],
        ]
      : []),
    ...(stats.longestStay
      ? [
          [
            t('stats.longestStay'),
            stats.longestStay.name,
            t('stats.nights', { count: stats.longestStay.value }),
          ] as [string, string, string],
        ]
      : []),
    ...(stats.highest
      ? [
          [
            t('stats.highest'),
            stats.highest.name,
            `${stats.highest.value.toLocaleString(lang)} m`,
          ] as [string, string, string],
        ]
      : []),
  ];
  const earned = stats.badges.filter((badge) => badge.done).length;
  return (
    <div className="page stats-page">
      <header className="stats-head">
        <div>
          <h1>{t('stats.title')}</h1>
          <p className="lead">{t(alone ? 'stats.leadTrip' : 'stats.lead')}</p>
        </div>
        <div className="stats-scope">
          <div className="field">
            <span className="field-label" id="stats-trips-label">
              {t('stats.tripsPick')}
            </span>
            <Popover
              open={choosing}
              onOpenChange={setChoosing}
              label={t('stats.tripsPick')}
              trigger={(props) => (
                <button
                  type="button"
                  className="pick-trigger stats-trips"
                  aria-labelledby="stats-trips-label stats-trips-value"
                  {...props}
                >
                  <span id="stats-trips-value">{alone?.name ?? t('stats.allTrips')}</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              )}
            >
              {/* As the trip switch has them, the families as trees; its days beside each, as a
                  copy or a branch is told from its trip by them. */}
              <TripTree
                trips={summaries.filter((one) =>
                  stats.choices.some((choice) => choice.id === one.id),
                )}
                current={alone?.id ?? null}
                to={(one) => `/stats?trip=${one.id}`}
                detail={(one) => tripDays(one.start, one.end, lang)}
                onPicked={() => {
                  setChoosing(false);
                }}
                first={
                  <li>
                    <Link
                      to="/stats"
                      aria-current={alone ? undefined : 'page'}
                      onClick={() => {
                        setChoosing(false);
                      }}
                    >
                      {t('stats.allTrips')}
                    </Link>
                  </li>
                }
              />
            </Popover>
          </div>
          <div className="segmented" role="group" aria-label={t('stats.year')}>
            {[...stats.years, 'all' as const].map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={one === shown}
                onClick={() => {
                  setYear(one);
                  setParams({});
                }}
              >
                {one === 'all' ? t('stats.allYears') : one}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="stats-tiles">
        <div className="panel stats-tile">
          <span>{t('stats.onTheWay')}</span>
          <b>{kmOf(stats.km, lang)}</b>
          <span>{t('stats.byTrain', { share: share(train?.km ?? 0, stats.km) })}</span>
        </div>
        <div className="panel stats-tile">
          <span>{t('stats.nightsAway')}</span>
          <b>{stats.nights}</b>
          <span>{t('stats.trips', { count: stats.tripCount })}</span>
        </div>
        <div className="panel stats-tile">
          <span>{t('stats.countries')}</span>
          <b>{stats.countries.length}</b>
          <span>{t('stats.new', { count: stats.newCountries })}</span>
        </div>
        <div className="panel stats-tile">
          <span>{t('stats.towns')}</span>
          <b>{stats.towns}</b>
          <span>
            {stats.longestStay ? t('stats.longestTown', { town: stats.longestStay.name }) : '—'}
          </span>
        </div>
        <div className="panel stats-tile">
          <span>CO₂</span>
          <b>{(stats.kg / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })} t</b>
          <span>{t('stats.flown', { share: share(stats.kgFlown, stats.kg) })}</span>
        </div>
      </div>

      <div className="stats-two">
        <section className="panel" aria-labelledby="stats-modes">
          <h2 id="stats-modes" className="panel-title">
            {t('stats.modes')}
          </h2>
          {stats.modes.length === 0 && <p className="quiet">{t('stats.noWays')}</p>}
          <ul className="stats-modes">
            {stats.modes.map(({ mode, km }) => (
              <li key={mode}>
                <span>
                  {SIGNS[mode]} {t(`mode.${mode}`)}
                </span>
                <span className="stats-track">
                  <span
                    style={{
                      width: `${String(Math.max(1, Math.round((km / widest) * 100)))}%`,
                      background: `var(--way-${mode === 'foot' ? 'plan' : mode})`,
                    }}
                  />
                </span>
                <span className="mono">{kmOf(km, lang)}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="panel" aria-labelledby="stats-longest">
          <h2 id="stats-longest" className="panel-title">
            {t('stats.longest')}
          </h2>
          {longest.length === 0 ? (
            <p className="quiet">{t('stats.noWays')}</p>
          ) : (
            <table className="data">
              <tbody>
                {longest.map(([what, name, value]) => (
                  <tr key={what}>
                    <td>{what}</td>
                    <td>{name}</td>
                    <td className="mono stats-n">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {calendar !== null && <YearOf stats={stats} year={calendar} />}

      <ExploredMap explored={stats.explored} />

      {/* Achievements are of all one's trips, not of one alone. */}
      {!alone && (
        <section className="panel" aria-labelledby="stats-badges">
          <div className="panel-head">
            <h2 id="stats-badges" className="panel-title">
              {t('stats.badges')}
            </h2>
            <span className="small quiet">
              {earned} / {stats.badges.length}
            </span>
          </div>
          <ul className="stats-badges">
            {stats.badges.map((badge) => (
              <BadgeCard key={badge.key} badge={badge} />
            ))}
          </ul>
        </section>
      )}

      <section className="panel" aria-labelledby="stats-studio">
        <h2 id="stats-studio" className="panel-title">
          {t('stats.studio')}
        </h2>
        <Studio key={stats.trips.map((trip) => trip.id).join()} trips={stats.trips} />
      </section>
    </div>
  );
}
